'use client';
// Plan de continuité d'activité — ISO 15189:2022 §7.8.
//
// Le produit savait déjà réagir à l'urgence documentaire : retrait d'urgence,
// gel d'applicabilité. Il ne savait pas dire ce que fait le laboratoire quand
// un automate tombe, quand le système informatique est indisponible, quand
// l'électricité manque.
//
// Ce que cet écran refuse de faire, et c'est le point :
//
//  · PROPOSER DES SITUATIONS TOUTES FAITES. Le registre part vide et le reste.
//    Un plan de continuité pré-rempli par le logiciel serait un plan que
//    personne n'a décidé, que personne ne saurait appliquer, et qui afficherait
//    en audit une couverture qui n'existe pas.
//
//  · LAISSER UN REGISTRE VIDE PASSER POUR UNE COUVERTURE. « 0 situation » se lit
//    trop facilement « rien à signaler ». L'écran dit donc explicitement ce
//    qu'un registre vide signifie, et — dès qu'il y a des lignes — combien de
//    natures de perturbation ne sont couvertes par RIEN.
//
//  · TRAITER « JAMAIS ÉPROUVÉE » COMME « À JOUR ». Un plan jamais éprouvé n'est
//    pas un plan, c'est une intention.
import React, { useEffect, useMemo, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { BoutonJournal } from '@/components/BoutonJournal';
import { ligneOuvrable, SansOuvrir } from '@/shared/Tableau';
import { useGed, userName } from '@/lib/store';
import { fmtDate } from '@/lib/meta';
import {
  I, card, inputStyle, EmptyState, FilterBar, RefISO,
  FieldError, useBusy } from '@/components/ui';
import { H1, lbl } from './ressources/commun';
import { useFormulaire } from '@/components/Formulaire';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
type Ligne = {
  documentId: string; ref: string; titre: string;
  categorie: string; categorieLabel: string;
  risqueCouvert: string | null; parade: string | null;
  decideur: string | null; executant: string | null;
  delaiActivation: string | null; moyens: string | null;
  impactPatients: string | null; docLie: string | null; risqueRef: string | null;
  statut: string;
  derniereEpreuve: string; natureEpreuve: string | null;
  verdictEpreuve: string | null; compteRenduEpreuve: string | null;
  prochaineEpreuve: string;
  dateRecensement: string; recensePar: string | null;
  sansExecutant: boolean; sansDecideur: boolean;
  jamaisEprouvee: boolean; epreuveEchue: boolean;
};
type Dossier = {
  lignes: Ligne[];
  categories: { code: string; label: string }[];
  verdicts: string[]; natures: string[]; statuts: string[];
  compte: {
    total: number; actives: number; obsoletes: number;
    jamaisEprouvees: number; epreuveEchue: number; sansExecutant: number;
    categoriesCouvertes: number; categoriesTotal: number;
  };
  categoriesNonCouvertes: { code: string; label: string }[];
};

const VERDICTS: Record<string, { label: string; bg: string; fg: string }> = {
  concluante: { label: 'Épreuve concluante', bg: 'var(--green-50)', fg: 'var(--green-600)' },
  reserves: { label: 'Concluante avec réserves', bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  non_concluante: { label: 'Non concluante', bg: 'var(--red-50)', fg: 'var(--red-600)' },
};
const NATURES: Record<string, string> = {
  exercice: 'exercice programmé',
  reel: 'événement réel',
};
const STATUTS: Record<string, { label: string; bg: string; fg: string }> = {
  redige: { label: 'Rédigée', bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
  valide: { label: 'Validée', bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' },
  obsolete: { label: 'Obsolète', bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
};

/** Saisie d'une situation. La parade est exigée : une situation sans parade
 *  relève du registre des risques (§8.5), pas du plan de continuité — ce
 *  registre-ci existe pour porter la réponse, pas la question. */
function Formulaire({ ligne, categories, statuts, onFait, onAnnule }: {
  ligne: Ligne | null; categories: { code: string; label: string }[]; statuts: string[];
  onFait: () => void; onAnnule: () => void;
}) {
  const { settings } = useGed();
  const gedUsers = settings?.gedUsers || [];
  const [f, setF] = useState(() => ({
    titre: ligne?.titre || '', categorie: ligne?.categorie || '',
    risqueCouvert: ligne?.risqueCouvert || '', parade: ligne?.parade || '',
    decideur: ligne?.decideur || '', executant: ligne?.executant || '',
    delaiActivation: ligne?.delaiActivation || '', moyens: ligne?.moyens || '',
    impactPatients: ligne?.impactPatients || '', docLie: ligne?.docLie || '',
    risqueRef: ligne?.risqueRef || '', statut: ligne?.statut || 'redige',
    prochaineEpreuve: ligne?.prochaineEpreuve || '',
  }));
  /* Ce que l'écran a lu, figé à l'ouverture (14/10/2026) : en modification,
     le moteur n'écrit que les rubriques changées depuis cette lecture. */
  const [base] = useState(() => f);
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const maj = (k: string, v: unknown) => setF((x) => ({ ...x, [k]: v }));

  /* Deux règles annoncées ENSEMBLE : le formulaire refusait au premier
     obstacle, on corrigeait, on recliquait, on découvrait le second. */
  const declaration = React.useMemo(() => [
    { id: 'c-titre', label: 'Intitulé', valeur: f.titre, requis: true },
    { id: 'c-cat', label: 'Nature de la perturbation', valeur: f.categorie, requis: true },
  ], [f.titre, f.categorie]);
  const { ref: refFormulaire, verifier, Bandeau } = useFormulaire(declaration);

  const enregistrer = () => run('save', async () => {
    if (!verifier()) return;
    if (!f.parade.trim()) { setErreur('Décrivez la parade : sans elle, la ligne relève du registre des risques (§8.5).'); return; }
    setErreur('');
    const r = await fetchEcriture(`/api/ged/ged/continuite${ligne ? `/${ligne.documentId}` : ''}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(ligne ? { ...f, base } : f),
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setErreur(j?.error?.message || 'Enregistrement refusé.');
      return;
    }
    onFait();
  });

  const personnes = (
    <>
      <option value="">— non désigné —</option>
      {gedUsers.map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
    </>
  );

  return (
    <div ref={refFormulaire} style={{ ...card, display: 'grid', gap: 12 }}>
      <Bandeau />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 260px', gap: 10 }}>
        <div>
          <label style={lbl} htmlFor="c-titre">Situation</label>
          <input id="c-titre" value={f.titre} maxLength={200} onChange={(e) => maj('titre', e.target.value)}
            placeholder="Coupure d’alimentation électrique prolongée (délestage)"
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }} />
        </div>
        <div>
          <label style={lbl} htmlFor="c-cat">Nature de la perturbation</label>
          <select id="c-cat" value={f.categorie} onChange={(e) => maj('categorie', e.target.value)}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
            <option value="">— choisir —</option>
            {categories.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label style={lbl} htmlFor="c-risque">Risque couvert — que se passe-t-il si rien n’est fait ?</label>
        <textarea id="c-risque" value={f.risqueCouvert} rows={2} onChange={(e) => maj('risqueCouvert', e.target.value)}
          style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
      </div>
      <div>
        <label style={lbl} htmlFor="c-parade">Parade — ce qu’on fait, concrètement</label>
        <textarea id="c-parade" value={f.parade} rows={3} onChange={(e) => maj('parade', e.target.value)}
          placeholder="Écrivez-la comme une consigne applicable par quelqu’un qui découvre la situation."
          style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <div>
          <label style={lbl} htmlFor="c-dec" title="Qui a autorité pour activer la parade. Sans décideur, personne ne l’active.">Qui décide</label>
          <select id="c-dec" value={f.decideur} onChange={(e) => maj('decideur', e.target.value)}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>{personnes}</select>
        </div>
        <div>
          <label style={lbl} htmlFor="c-exe" title="Qui met la parade en œuvre. Une parade sans exécutant désigné ne se déclenche pas.">Qui exécute</label>
          <select id="c-exe" value={f.executant} onChange={(e) => maj('executant', e.target.value)}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>{personnes}</select>
        </div>
        <div>
          <label style={lbl} htmlFor="c-delai">Délai d’activation</label>
          <input id="c-delai" value={f.delaiActivation} maxLength={80} onChange={(e) => maj('delaiActivation', e.target.value)}
            placeholder="Immédiat, < 30 min…" style={{ ...inputStyle, height: 34, fontSize: 12.5 }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={lbl} htmlFor="c-moyens">Moyens nécessaires</label>
          <textarea id="c-moyens" value={f.moyens} rows={2} onChange={(e) => maj('moyens', e.target.value)}
            placeholder="Groupe électrogène, laboratoire partenaire, procédure dégradée papier…"
            style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
        </div>
        <div>
          <label style={lbl} htmlFor="c-impact">Conséquence pour les patients</label>
          <textarea id="c-impact" value={f.impactPatients} rows={2} onChange={(e) => maj('impactPatients', e.target.value)}
            placeholder="Délai de rendu allongé, examens reportés, sous-traitance…"
            style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
        <div>
          <label style={lbl} htmlFor="c-doc">Document de référence</label>
          <input id="c-doc" value={f.docLie} maxLength={60} onChange={(e) => maj('docLie', e.target.value)}
            placeholder="Code du mode opératoire" style={{ ...inputStyle, height: 34, fontSize: 12.5 }} />
        </div>
        <div>
          <label style={lbl} htmlFor="c-rsk" title="Référence de la fiche du registre des risques dont cette situation est la parade, s’il y en a une.">Risque lié (§8.5)</label>
          <input id="c-rsk" value={f.risqueRef} maxLength={60} onChange={(e) => maj('risqueRef', e.target.value)}
            placeholder="BIO24-RSK-2026-001" style={{ ...inputStyle, height: 34, fontSize: 12.5, fontFamily: 'var(--font-mono)' }} />
        </div>
        <div>
          <label style={lbl} htmlFor="c-proch">Prochaine épreuve prévue</label>
          <input id="c-proch" type="date" value={f.prochaineEpreuve} onChange={(e) => maj('prochaineEpreuve', e.target.value)}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }} />
        </div>
        <div>
          <label style={lbl} htmlFor="c-statut">Statut</label>
          <select id="c-statut" value={f.statut} onChange={(e) => maj('statut', e.target.value)}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
            {statuts.map((s) => <option key={s} value={s}>{STATUTS[s]?.label || s}</option>)}
          </select>
        </div>
      </div>

      <FieldError>{erreur}</FieldError>
      <PiedActions taille="sm" annuler={{ onClick: onAnnule }} principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe(), onClick: () => void enregistrer() }} />
    </div>
  );
}

/** Enregistrement d'une épreuve. Le compte rendu est exigé : « concluante »
 *  écrit seul est une case cochée, pas une épreuve. */
function FormEpreuve({ ligne, verdicts, natures, onFait, onAnnule }: {
  ligne: Ligne; verdicts: string[]; natures: string[]; onFait: () => void; onAnnule: () => void;
}) {
  const [date, setDate] = useState('');
  const [nature, setNature] = useState('exercice');
  const [verdict, setVerdict] = useState('concluante');
  const [compteRendu, setCompteRendu] = useState('');
  const [prochaine, setProchaine] = useState(ligne.prochaineEpreuve || '');
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();

  const envoyer = () => run('save', async () => {
    setErreur('');
    const r = await fetchEcriture(`/api/ged/ged/continuite/${ligne.documentId}/epreuve`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date, nature, verdict, compteRendu, prochaine }),
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setErreur(j?.error?.message || 'Enregistrement refusé.');
      return;
    }
    onFait();
  });

  return (
    <div style={{ ...card, display: 'grid', gap: 12, background: 'var(--surface-sunken)' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>
        Éprouver la parade — {ligne.ref} · {ligne.titre}
      </div>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        Un <strong>événement subi</strong> vaut épreuve autant qu’un exercice programmé : si la
        parade a tenu pendant une coupure réelle, ça se prouve mieux qu’une simulation. Les
        distinguer sert seulement à ne pas confondre « on a essayé » avec « ça nous est arrivé ».
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
        <div>
          <label style={lbl} htmlFor="e-date">Date de l’épreuve</label>
          <input id="e-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }} />
        </div>
        <div>
          <label style={lbl} htmlFor="e-nature">Nature</label>
          <select id="e-nature" value={nature} onChange={(e) => setNature(e.target.value)}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
            {natures.map((n) => <option key={n} value={n}>{NATURES[n] || n}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl} htmlFor="e-verdict">Verdict</label>
          <select id="e-verdict" value={verdict} onChange={(e) => setVerdict(e.target.value)}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
            {verdicts.map((v) => <option key={v} value={v}>{VERDICTS[v]?.label || v}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl} htmlFor="e-proch">Prochaine épreuve</label>
          <input id="e-proch" type="date" value={prochaine} onChange={(e) => setProchaine(e.target.value)}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }} />
        </div>
      </div>
      <div>
        <label style={lbl} htmlFor="e-cr">Compte rendu — ce qui s’est passé, ce qui a manqué</label>
        <textarea id="e-cr" value={compteRendu} rows={3} onChange={(e) => setCompteRendu(e.target.value)}
          placeholder="Un verdict seul ne prouve rien : dites ce qui a été fait, en combien de temps, et ce qui a coincé."
          style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
      </div>
      <FieldError>{erreur}</FieldError>
      <PiedActions taille="sm" annuler={{ onClick: onAnnule }} principale={{ libelle: 'Enregistrer l’épreuve', libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe(), onClick: () => void envoyer() }} />
    </div>
  );
}

export function Continuite() {
  const { session, settings } = useGed();
  const isRQ = session.roles.includes('rq');
  const [d, setD] = useState<Dossier | null>(null);
  const [chargement, setChargement] = useState(true);
  const [tour, setTour] = useState(0);
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const [saisie, setSaisie] = useState(false);
  const [edite, setEdite] = useState<Ligne | null>(null);
  const [epreuve, setEpreuve] = useState<Ligne | null>(null);

  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/continuite', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setD(j); })
      .catch(() => { if (vivant) setD(null); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [tour]);

  const recharger = () => { setSaisie(false); setEdite(null); setEpreuve(null); setTour((t) => t + 1); };

  const filtrees = useMemo(() => {
    const base = (d?.lignes || []).filter((l) => (
      filtre === 'jamais' ? l.jamaisEprouvee && l.statut !== 'obsolete'
        : filtre === 'echues' ? l.epreuveEchue
          : filtre === 'sansexec' ? l.sansExecutant && l.statut !== 'obsolete'
            : filtre === 'obsoletes' ? l.statut === 'obsolete'
              : l.statut !== 'obsolete'));
    const qq = q.trim().toLowerCase();
    return qq
      ? base.filter((l) => `${l.ref} ${l.titre} ${l.categorieLabel} ${l.parade}`.toLowerCase().includes(qq))
      : base;
  }, [d, filtre, q]);

  if (chargement) return <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Chargement du plan de continuité…</p>;
  if (!d) return null;
  const { compte } = d;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <H1 eyebrow={<>Continuité · <RefISO code="7.8" /></>} title="Plan de continuité d’activité" />

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        Que fait le laboratoire quand un automate tombe, quand le système informatique est
        indisponible, quand l’électricité manque ? Une ligne par situation, et cinq questions :
        le risque couvert, la parade, <strong>qui décide</strong>, <strong>qui exécute</strong>,
        et <strong>quand la parade a été éprouvée</strong> pour la dernière fois. La dernière est
        celle qui compte : un plan jamais éprouvé n’est pas un plan, c’est une intention.
      </p>

      {compte.total === 0 && !saisie && (
        <>
          <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
            <strong style={{ color: 'var(--text-strong)' }}>Le registre est vide : aucune situation n’a été recensée.</strong>{' '}
            Ce n’est pas « aucun risque de discontinuité » — c’est que la question n’a pas encore
            été instruite. Rien n’est pré-rempli ici, et c’est délibéré : un plan de continuité
            fabriqué par le logiciel serait un plan que personne n’a décidé, et il afficherait en
            audit une couverture qui n’existe pas.
          </div>
          <EmptyState
            titre="Aucune situation de continuité n’est recensée."
            indice="Commencez par ce qui vous est déjà arrivé : une coupure de courant, une panne d’automate, une indisponibilité du serveur. Une situation vécue se décrit mieux qu’une situation imaginée."
            action={isRQ ? (
              <Bouton variante="primaire" onClick={() => setSaisie(true)}>
                <I n="plus" s={14} />Recenser la première
              </Bouton>
            ) : undefined} />
        </>
      )}

      {compte.total > 0 && d.categoriesNonCouvertes.length > 0 && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text-strong)' }}>
            {compte.categoriesCouvertes} nature{compte.categoriesCouvertes > 1 ? 's' : ''} de perturbation
            couverte{compte.categoriesCouvertes > 1 ? 's' : ''} sur {compte.categoriesTotal}.
          </strong>{' '}
          Rien n’est prévu pour : {d.categoriesNonCouvertes.map((c) => c.label).join(', ')}. Ce
          n’est pas nécessairement un défaut — toutes ne concernent pas tous les laboratoires —
          mais c’est une question à trancher explicitement plutôt qu’à laisser ouverte.
        </div>
      )}

      {compte.jamaisEprouvees > 0 && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          <strong>{compte.jamaisEprouvees} parade{compte.jamaisEprouvees > 1 ? 's' : ''} jamais éprouvée{compte.jamaisEprouvees > 1 ? 's' : ''}.</strong>{' '}
          Un événement subi compte autant qu’un exercice — si une coupure réelle s’est bien
          passée, enregistrez-la : c’est la meilleure preuve qu’il y ait.
        </div>
      )}

      {(saisie || edite) && isRQ && (
        <Formulaire ligne={edite} categories={d.categories} statuts={d.statuts}
          onFait={recharger} onAnnule={() => { setSaisie(false); setEdite(null); }} />
      )}
      {epreuve && isRQ && (
        <FormEpreuve ligne={epreuve} verdicts={d.verdicts} natures={d.natures}
          onFait={recharger} onAnnule={() => setEpreuve(null)} />
      )}

      {compte.total > 0 && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <FilterBar
                q={q} onQ={setQ} placeholder="Rechercher une situation, une parade…"
                chips={[
                  { k: 'tous', label: 'Actives', n: compte.actives },
                  { k: 'jamais', label: 'Jamais éprouvées', n: compte.jamaisEprouvees },
                  { k: 'echues', label: 'Épreuve échue', n: compte.epreuveEchue },
                  { k: 'sansexec', label: 'Sans exécutant', n: compte.sansExecutant },
                  { k: 'obsoletes', label: 'Obsolètes', n: compte.obsoletes },
                ]}
                actif={filtre} onChip={setFiltre}
                total={compte.total} affiches={filtrees.length}
                onReset={() => { setQ(''); setFiltre('tous'); }} />
            </div>
            {isRQ && !saisie && !edite && (
              <Bouton variante="primaire" taille="sm" onClick={() => setSaisie(true)}>
                <I n="plus" s={13} />Recenser
              </Bouton>
            )}
          </div>

          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {filtrees.map((l, i) => {
              const st = STATUTS[l.statut] || STATUTS.redige;
              const v = l.verdictEpreuve ? VERDICTS[l.verdictEpreuve] : null;
              return (
                /* Le clic ouvre la parade ; « Éprouver » reste, ce n'est pas
                   le geste par défaut (Ines, 26/08/2026). */
                <div key={l.documentId}
                  {...(isRQ
                    ? ligneOuvrable(() => { setSaisie(false); setEpreuve(null); setEdite(l); }, `Modifier ${l.ref}`,
                      { padding: '11px 14px', borderTop: i ? '1px solid var(--border-subtle)' : 'none' })
                    : { style: { padding: '11px 14px', borderTop: i ? '1px solid var(--border-subtle)' : 'none' } })}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{l.ref}</span>
                    {/* La traçabilité au même endroit que partout : l'icône du
                        journal suit la référence (règle RQ du 18/08, câblée le
                        24/08/2026 — ce registre écrivait au journal sans le
                        montrer). */}
                    <SansOuvrir><BoutonJournal reference={l.ref} quoi="de cette parade" /></SansOuvrir>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{l.titre}</span>
                    <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: st.bg, color: st.fg }}>{st.label}</span>
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{l.categorieLabel}</span>
                    <span style={{ flex: 1 }} />
                    {l.jamaisEprouvee ? (
                      <span title="Une parade jamais mise à l’épreuve n’est pas un plan, c’est une intention."
                        style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)', cursor: 'help' }}>
                        jamais éprouvée
                      </span>
                    ) : v && (
                      <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: v.bg, color: v.fg }}>
                        {v.label} · {fmtDate(l.derniereEpreuve)}
                        {l.natureEpreuve ? ` (${NATURES[l.natureEpreuve] || l.natureEpreuve})` : ''}
                      </span>
                    )}
                  </div>

                  {l.parade && (
                    <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginTop: 5, lineHeight: 1.5 }}>{l.parade}</div>
                  )}

                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 5, fontSize: 12.5, color: 'var(--text-muted)' }}>
                    <span style={{ color: l.sansDecideur ? 'var(--amber-600)' : undefined, fontWeight: l.sansDecideur ? 700 : 400 }}>
                      décide : {l.decideur ? userName(settings, l.decideur) : 'non désigné'}
                    </span>
                    <span style={{ color: l.sansExecutant ? 'var(--amber-600)' : undefined, fontWeight: l.sansExecutant ? 700 : 400 }}>
                      exécute : {l.executant ? userName(settings, l.executant) : 'non désigné'}
                    </span>
                    {l.delaiActivation && <span>délai : {l.delaiActivation}</span>}
                    {l.docLie && <span>document : {l.docLie}</span>}
                    {l.risqueRef && <span>risque {l.risqueRef}</span>}
                    {l.prochaineEpreuve && (
                      <span style={{ color: l.epreuveEchue ? 'var(--red-600)' : undefined, fontWeight: l.epreuveEchue ? 700 : 400 }}>
                        prochaine épreuve {fmtDate(l.prochaineEpreuve)}{l.epreuveEchue ? ' — échue' : ''}
                      </span>
                    )}
                    <span style={{ flex: 1 }} />
                    {isRQ && (
                      <SansOuvrir>
                        <Bouton taille="sm" onClick={() => { setSaisie(false); setEdite(null); setEpreuve(l); }} title="Enregistrer un exercice ou un événement réel qui a mis cette parade à l’épreuve.">
                          Éprouver
                        </Bouton>
                      </SansOuvrir>
                    )}
                  </div>

                  {l.compteRenduEpreuve && (
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5, borderInlineStart: '2px solid var(--border-subtle)', paddingInlineStart: 9 }}>
                      {l.compteRenduEpreuve}
                    </div>
                  )}
                </div>
              );
            })}
            {filtrees.length === 0 && (
              <div style={{ padding: 16 }}>
                <EmptyState titre="Aucune situation ne correspond à ces critères."
                  action={<Bouton taille="sm" onClick={() => { setQ(''); setFiltre('tous'); }}>Réinitialiser</Bouton>} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
